import { useEffect } from 'react';
import { type Editor } from '@tiptap/react';
import {
  INSPECTION_LAYOUT_CSS,
  getLayout,
  layoutClass,
  slotClass,
  blockClass,
  type InspectionPage,
  type RichText,
  type PhotoTransform,
  type Annotation,
  type AnnoColor,
} from '@jobos/shared';

import { cn } from '@/lib/utils';
import { clampPanOffset } from '@/lib/inspectionDocument';
import { InlineRichText } from './InlineRichText';
import { CaptionField } from './CaptionField';
import { AnnotationLayer, type AnnotationTool } from './AnnotationLayer';
import { SlotDropWrapper } from './SlotDropWrapper';

// Injects the shared layout CSS into one <style> tag. The editor and the PDF
// generator both render from this same stylesheet + the same class names, which
// is the parity contract (see the design spec).
const STYLE_ID = 'ins-layout-css';
export function useInspectionLayoutCss() {
  useEffect(() => {
    let el = document.getElementById(STYLE_ID);
    if (!el) {
      el = document.createElement('style');
      el.id = STYLE_ID;
      document.head.appendChild(el);
    }
    // Write the current CSS every time rather than only on create: the tag
    // outlives an HMR module reload, so a create-only guard would leave the
    // previous build's geometry in the page.
    if (el.textContent !== INSPECTION_LAYOUT_CSS) el.textContent = INSPECTION_LAYOUT_CSS;
  }, []);
}

function RichTextView({ value }: { value: RichText | undefined }) {
  if (!value || value.length === 0) {
    return <span className="ins-canvas-placeholder">Click to add text</span>;
  }
  return (
    <>
      {value.map((block, i) => {
        const content = block.runs.map((run, j) => {
          let node: React.ReactNode = run.text;
          if (run.underline) node = <u key={`u${j}`}>{node}</u>;
          if (run.italic) node = <em key={`i${j}`}>{node}</em>;
          if (run.bold) node = <strong key={`b${j}`}>{node}</strong>;
          return <span key={j}>{node}</span>;
        });
        return block.style === 'heading' ? <h2 key={i} className="ins-h">{content}</h2> : <p key={i} className="ins-p">{content}</p>;
      })}
    </>
  );
}

export interface PageCanvasProps {
  page: InspectionPage;
  /** Presigned URLs keyed by file id — the same value a slot stores. */
  photoUrls: Record<string, string>;
  selectedRegion?: { kind: 'slot' | 'block'; key: string } | null;
  onSelectSlot?: (slotKey: string) => void;
  onSelectBlock?: (blockKey: string) => void;
  /** Edit a text block directly on the page. When omitted, blocks render read-only. */
  onChangeBlock?: (blockKey: string, value: RichText) => void;
  /** Receives the focused block's editor so the inspector can show its toolbar. */
  onActiveEditor?: (editor: Editor) => void;
  /** Drag-to-pan a slot's photo. When omitted, photos are not pannable. */
  onChangeTransform?: (slotKey: string, transform: PhotoTransform) => void;
  /** Edit a slot's caption on the page. When omitted, captions render read-only. */
  onChangeCaption?: (slotKey: string, caption: string) => void;
  /** Annotation editing for the selected slot. */
  annotationTool?: AnnotationTool;
  selectedAnnotationId?: string | null;
  onSelectAnnotation?: (id: string | null) => void;
  /** Free-floating page-level annotations + their editing handler. */
  pageAnnotations?: Annotation[];
  onChangePageAnnotations?: (annotations: Annotation[]) => void;
  /** Sizes newly drawn text/arrows are created with (inspector's last pick). */
  newTextSize?: number;
  newArrowHeadSize?: number;
  /** Color newly drawn annotations are created with (inspector's last pick). */
  newColor?: AnnoColor;
  /** Upload an image dropped onto a slot. When omitted, slots are not drop targets. */
  onDropPhoto?: (slotKey: string, files: File[]) => void;
  /**
   * Open the job photo library for an empty slot. When omitted the placeholder
   * is inert text, which is what the sidebar thumbnails want.
   */
  onPickFromLibrary?: (slotKey: string) => void;
  /** Key of the slot currently uploading, if any — shows a spinner and blocks re-drops. */
  uploadingSlotKey?: string | null;
}

// Generic, registry-driven page renderer. No per-layout code — it iterates the
// layout definition and stamps the shared classes, so adding a layout needs only
// a registry entry + CSS block.
export function PageCanvas({ page, photoUrls, selectedRegion, onSelectSlot, onSelectBlock, onChangeBlock, onActiveEditor, onChangeTransform, onChangeCaption, annotationTool, selectedAnnotationId, onSelectAnnotation, pageAnnotations, onChangePageAnnotations, newTextSize, newArrowHeadSize, newColor, onDropPhoto, onPickFromLibrary, uploadingSlotKey }: PageCanvasProps) {
  useInspectionLayoutCss();
  const layout = getLayout(page.layoutType);
  // Read-only callers (thumbnails) pass only `page`; the editor passes the live
  // array it's editing.
  const pageAnnos = pageAnnotations ?? page.annotations ?? [];

  // Drag the photo within its slot. Deltas are normalized by the slot size and
  // clamped so the (zoomed) photo edge can't be pulled inside the frame.
  const startPan = (e: React.MouseEvent, slotKey: string, transform: PhotoTransform, el: HTMLElement) => {
    if (!onChangeTransform || transform.zoom <= 1) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const sx = e.clientX;
    const sy = e.clientY;
    const { offsetX: ox, offsetY: oy, zoom } = transform;
    const move = (ev: MouseEvent) => {
      const dx = (ev.clientX - sx) / rect.width;
      const dy = (ev.clientY - sy) / rect.height;
      onChangeTransform(slotKey, {
        zoom,
        offsetX: clampPanOffset(ox + dx, zoom),
        offsetY: clampPanOffset(oy + dy, zoom),
      });
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  return (
    <div className="ins-canvas-page-wrap">
      {/* Mousedowns on annotations stopPropagation, so anything reaching here is
          a click on empty space / another region → clear annotation selection. */}
      <div
        className={cn('ins-page ins-canvas-page', layoutClass(page.layoutType))}
        onMouseDown={() => onSelectAnnotation?.(null)}
      >
        {layout.slots.map((s) => {
          const slot = page.slots[s.key];
          const url = slot?.fileId ? photoUrls[slot.fileId] : undefined;
          const t = slot?.transform ?? { zoom: 1, offsetX: 0, offsetY: 0 };
          const isSelected = selectedRegion?.kind === 'slot' && selectedRegion.key === s.key;
          const isUploading = uploadingSlotKey === s.key;
          return (
            <SlotDropWrapper
              key={s.key}
              slotKey={s.key}
              onDropPhoto={onDropPhoto ?? (() => {})}
              disabled={!onDropPhoto || isUploading}
              className={cn('ins-slot', slotClass(s.key), isSelected && 'ins-region-selected')}
              onClick={() => onSelectSlot?.(s.key)}
              data-slot-key={s.key}
              role="button"
              tabIndex={0}
            >
              {/* Always rendered, even empty: the reserved line keeps every photo
                  the same size whether or not it has a caption. */}
              {onChangeCaption ? (
                <CaptionField
                  key={`${page.id}:${s.key}`}
                  value={slot?.caption ?? ''}
                  onChange={(caption) => onChangeCaption(s.key, caption)}
                />
              ) : (
                <div className="ins-caption">{slot?.caption ?? ''}</div>
              )}
              <div className="ins-slot-frame">
                {url ? (
                  <div
                    className="ins-photo-layer"
                    // A zoomed photo centres itself in the frame instead of
                    // staying left-aligned; the shared CSS keys off this
                    // attribute, and the PDF sets it from the same condition.
                    data-zoomed={t.zoom > 1 ? 'true' : undefined}
                    style={{
                      transform: `translate(${t.offsetX * 100}%, ${t.offsetY * 100}%) scale(${t.zoom})`,
                      cursor: onChangeTransform && t.zoom > 1 ? 'grab' : undefined,
                    }}
                    onMouseDown={(e) => startPan(e, s.key, t, e.currentTarget)}
                  >
                    <img className="ins-photo" src={url} alt={slot?.caption ?? ''} draggable={false} />
                  </div>
                ) : (
                  <span className="ins-canvas-placeholder">
                    Add photo
                    {onPickFromLibrary && (
                      // The library is the shortcut worth putting on the page:
                      // the photos are usually already on the job, and the
                      // upload button in the inspector covers the rest.
                      <button
                        type="button"
                        className="ins-slot-pick-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectSlot?.(s.key);
                          onPickFromLibrary(s.key);
                        }}
                      >
                        Choose from job photos
                      </button>
                    )}
                  </span>
                )}
                {/* Legacy per-slot annotation overlay — display-only. Any not-yet-
                    lifted slot annotations still render here; editing now happens on
                    the page-level layer (annotations are auto-lifted to the page). */}
                {url && slot && (
                  <AnnotationLayer
                    annotations={slot.annotations}
                    interactive={false}
                    tool="select"
                    selectedId={null}
                    onChange={() => {}}
                    onSelect={() => {}}
                  />
                )}
                {isUploading ? <div className="ins-slot-uploading" aria-label="Uploading photo" /> : null}
              </div>
            </SlotDropWrapper>
          );
        })}
        {layout.blocks.map((b) => {
          const isSelected = selectedRegion?.kind === 'block' && selectedRegion.key === b.key;
          return (
            <div
              key={b.key}
              className={cn('ins-block', blockClass(b.key), isSelected && 'ins-region-selected')}
            >
              {onChangeBlock ? (
                <InlineRichText
                  key={`${page.id}:${b.key}`}
                  value={page.blocks[b.key] ?? []}
                  vAlign={page.blockMeta?.[b.key]?.vAlign}
                  onChange={(rt) => onChangeBlock(b.key, rt)}
                  onFocusEditor={(ed) => {
                    onSelectBlock?.(b.key);
                    onActiveEditor?.(ed);
                  }}
                />
              ) : (
                <RichTextView value={page.blocks[b.key]} />
              )}
            </div>
          );
        })}
        {/* Page-level annotation overlay — stretched over the whole page (above
            slots + blocks), the layer annotations are drawn on. Interactive on
            the editor canvas; read-only wherever the canvas is rendered as a
            preview (sidebar thumbnails), which still has to show them. */}
        {pageAnnos.length > 0 || onChangePageAnnotations ? (
          <AnnotationLayer
            svgClassName="ins-page-anno-svg"
            annotations={pageAnnos}
            interactive={!!onChangePageAnnotations}
            tool={annotationTool ?? 'select'}
            selectedId={onChangePageAnnotations ? selectedAnnotationId ?? null : null}
            onChange={onChangePageAnnotations ?? (() => {})}
            onSelect={(id) => onSelectAnnotation?.(id)}
            newTextSize={newTextSize}
            newArrowHeadSize={newArrowHeadSize}
            newColor={newColor}
          />
        ) : null}
      </div>
    </div>
  );
}
